import { useState } from 'react';
import { recipientGetCase, sendQiEmail } from '@/pages/og-qi-healing/lib/api';

export interface LayerResult {
  sentAt: string | null;
  fetchStatus: number | null;
  httpStatus: number | null;
  resendId: string | null;
  success: boolean | null;
  message: string;
  reason: string | null;
  detail: string | null;
  from: string | null;
  domainConfigured: boolean | null;
  linkCheck: string | null;
}

interface LayerTestBlockProps {
  layer: number;
  title: string;
  description: string;
  showNames: boolean;
  enabled: boolean;
  lockHint: string;
  onSuccess: () => void;
}

const EDGE_FUNCTION = 'send_qi_email';

const EMPTY_RESULT: LayerResult = {
  sentAt: null,
  fetchStatus: null,
  httpStatus: null,
  resendId: null,
  success: null,
  message: '',
  reason: null,
  detail: null,
  from: null,
  domainConfigured: null,
  linkCheck: null,
};

function buildTestLink(): string {
  const basePath = (__BASE_PATH__ as string).split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';
  return `${window.location.origin}${pathPrefix}/og/qi-healing/follow-up/TEST-${Date.now()}`;
}

// 從安全連結網址解析出 token，並以 qi_recipient 的 get_case 驗證是否可開啟。
async function checkSecureLink(url: string): Promise<string> {
  if (!url.trim()) return '未提供安全連結';
  let token = '';
  try {
    const parsed = new URL(url);
    const parts = parsed.pathname.split('/').filter(Boolean);
    token = parts[parts.length - 1] || '';
  } catch {
    return '安全連結格式無效（非合法 URL）';
  }
  if (!token) return '無法從連結解析出 token';
  if (token.toUpperCase().startsWith('TEST')) {
    return '此為測試佔位連結（未驗證）；如需驗證真實可開啟，請貼上正式案例的安全連結。';
  }
  try {
    await recipientGetCase(token);
    return '安全連結有效（可開啟受施者回報頁）';
  } catch (err) {
    const code = (err as { code?: string }).code || 'UNKNOWN';
    const map: Record<string, string> = {
      TOKEN_INVALID: '連結無效（token 不匹配）',
      TOKEN_REVOKED: '連結已撤銷',
      TOKEN_EXPIRED: '連結已過期',
    };
    return map[code] || `安全連結檢查失敗：${code}`;
  }
}

function reasonLabel(reason?: string | null): string {
  switch (reason) {
    case 'missing_recipient_or_url':
      return '缺少收件 Email 或安全連結。';
    case 'email_service_not_configured':
      return 'Backend Secrets 尚未設定 RESEND_API_KEY 或 RESEND_FROM_DOMAIN。';
    case 'network_error':
      return '無法連線到寄信服務。';
    default:
      return reason || '未知原因';
  }
}

const inputCls =
  'w-full rounded-md border border-foreground-200 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400';

export default function LayerTestBlock({
  layer,
  title,
  description,
  showNames,
  enabled,
  lockHint,
  onSuccess,
}: LayerTestBlockProps) {
  const [email, setEmail] = useState('');
  const [testName, setTestName] = useState('');
  const [practitionerName, setPractitionerName] = useState('');
  const [recipientAlias, setRecipientAlias] = useState('');
  const [secureUrl, setSecureUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [checkingLink, setCheckingLink] = useState(false);
  const [result, setResult] = useState<LayerResult>(EMPTY_RESULT);

  const handleGenerateLink = () => {
    setSecureUrl(buildTestLink());
    setResult((prev) => ({ ...prev, linkCheck: null }));
  };

  const handleCheckLink = async () => {
    setCheckingLink(true);
    const check = await checkSecureLink(secureUrl.trim() || buildTestLink());
    setResult((prev) => ({ ...(prev ?? EMPTY_RESULT), linkCheck: check }));
    setCheckingLink(false);
  };

  const handleSend = async () => {
    if (!email.trim()) {
      setResult({ ...EMPTY_RESULT, success: false, message: '請填寫測試收件信箱。', linkCheck: null });
      return;
    }
    const url = secureUrl.trim() || buildTestLink();
    const startedAt = new Date();
    setSending(true);
    setResult(EMPTY_RESULT);
    try {
      const res = await sendQiEmail({
        recipient_email: email.trim(),
        recipient_alias: showNames ? recipientAlias.trim() || `受施者-TEST-${layer}` : undefined,
        practitioner_name: showNames ? practitionerName.trim() || `施炁者-TEST-${layer}` : undefined,
        secure_url: url,
      });
      const success = res.email_status === 'sent';
      setResult({
        sentAt: startedAt.toLocaleString('zh-TW', { hour12: false }),
        fetchStatus: res.fetch_status ?? null,
        httpStatus: res.http_status ?? null,
        resendId: res.resend_id ?? null,
        success,
        message: success ? 'Email 已成功寄出（Resend 已接收）。' : `Email 未寄出：${reasonLabel(res.reason)}`,
        reason: res.reason ?? null,
        detail: res.detail ?? null,
        from: res.from ?? null,
        domainConfigured: res.domain_configured ?? null,
        linkCheck: null,
      });
      if (success) onSuccess();
    } catch (err) {
      setResult({
        ...EMPTY_RESULT,
        sentAt: startedAt.toLocaleString('zh-TW', { hour12: false }),
        success: false,
        message: '寄送時發生未預期錯誤。',
        detail: err instanceof Error ? err.message : '未知錯誤',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      className={`rounded-lg border bg-white p-5 md:p-6 space-y-4 ${
        enabled ? 'border-foreground-200/70' : 'border-foreground-200/40 opacity-70'
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold">
              {layer}
            </span>
            <h2 className="text-base font-semibold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              第{['一', '二', '三'][layer - 1]}層 Email 測試
            </h2>
            <span className="whitespace-nowrap text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-900">TEST</span>
          </div>
          <p className="mt-1 text-sm text-foreground-600">{title}</p>
        </div>
      </div>

      <p className="text-xs text-foreground-500">{description}</p>

      {!enabled && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <i className="ri-lock-line" />
          {lockHint}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="block text-sm text-foreground-700 mb-1.5">測試收件信箱（必填）</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="請輸入要收信的測試信箱"
            disabled={!enabled}
            className={inputCls}
          />
        </div>

        <div>
          <label className="block text-sm text-foreground-700 mb-1.5">測試名稱／案件資料</label>
          <input
            type="text"
            value={testName}
            onChange={(e) => setTestName(e.target.value)}
            placeholder="例：TEST-第一層-基礎寄信"
            disabled={!enabled}
            className={inputCls}
          />
        </div>

        {showNames && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-foreground-700 mb-1.5">施炁者名稱</label>
              <input
                type="text"
                value={practitionerName}
                onChange={(e) => setPractitionerName(e.target.value)}
                placeholder="施炁者-TEST"
                disabled={!enabled}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-sm text-foreground-700 mb-1.5">受施者名稱</label>
              <input
                type="text"
                value={recipientAlias}
                onChange={(e) => setRecipientAlias(e.target.value)}
                placeholder="受施者-TEST"
                disabled={!enabled}
                className={inputCls}
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm text-foreground-700 mb-1.5">安全連結</label>
          <input
            type="text"
            value={secureUrl}
            onChange={(e) => setSecureUrl(e.target.value)}
            placeholder="留白則自動產生測試佔位連結"
            disabled={!enabled}
            className={inputCls}
          />
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button
              onClick={handleGenerateLink}
              disabled={!enabled}
              className="whitespace-nowrap px-3 py-1.5 rounded-md border border-foreground-200 text-xs text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              產生測試連結
            </button>
            <button
              onClick={handleCheckLink}
              disabled={!enabled || checkingLink}
              className="whitespace-nowrap px-3 py-1.5 rounded-md border border-foreground-200 text-xs text-foreground-600 hover:bg-foreground-50 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {checkingLink ? '檢查中…' : '檢查安全連結'}
            </button>
          </div>
          <p className="mt-1 text-[11px] text-foreground-400">留白時會自動產生「TEST-時間戳」佔位連結，僅供驗證寄信流程；要驗證連結真的能開啟，請貼上正式案例的安全連結。</p>
        </div>

        <button
          onClick={handleSend}
          disabled={!enabled || sending}
          className="w-full whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          {sending ? '寄送中……' : '寄送本層測試 Email'}
        </button>
      </div>

      {/* 結果面板 */}
      <ResultPanel result={result} testName={testName} />
    </section>
  );
}

function ResultPanel({ result, testName }: { result: LayerResult; testName: string }) {
  const hasRun = result.sentAt !== null || result.linkCheck !== null;

  if (!hasRun) {
    return (
      <div className="rounded-md border border-dashed border-foreground-200 px-4 py-3 text-xs text-foreground-400">
        尚未執行。填好欄位後點「寄送本層測試 Email」。
      </div>
    );
  }

  const statusTone = result.success === true
    ? 'border-accent-300 bg-accent-100/60'
    : result.success === false
      ? 'border-red-300 bg-red-50'
      : 'border-foreground-200 bg-background-50';

  return (
    <div className={`rounded-md border px-4 py-3 text-sm space-y-1.5 ${statusTone}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-medium text-foreground-900">
          {result.success === true ? '✓ ' : result.success === false ? '✗ ' : ''}
          {result.message || '已執行'}
        </p>
      </div>

      <Row label="測試名稱" value={testName || '—'} />
      <Row label="Edge Function" value={EDGE_FUNCTION} />
      <Row label="寄送時間" value={result.sentAt || '—'} />
      <Row label="HTTP 狀態（Resend）" value={result.httpStatus === null ? '—' : String(result.httpStatus)} />
      <Row label="HTTP 狀態（Edge Function）" value={result.fetchStatus === null ? '—' : String(result.fetchStatus)} />
      <Row label="Resend Email ID" value={result.resendId || '—'} />
      <Row label="寄件者" value={result.from || '—'} />
      <Row
        label="寄件網域設定"
        value={
          result.domainConfigured === null
            ? '—'
            : result.domainConfigured
              ? '已成功讀取'
              : '未讀取（缺少 RESEND_FROM_DOMAIN）'
        }
      />
      {result.reason && <Row label="錯誤代碼" value={result.reason} />}
      {result.detail && (
        <div className="text-xs">
          <span className="text-foreground-500">遮蔽後錯誤內容：</span>
          <span className="text-foreground-700 break-all">{result.detail}</span>
        </div>
      )}
      {result.linkCheck && <Row label="安全連結檢查結果" value={result.linkCheck} />}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-foreground-500 whitespace-nowrap">{label}：</span>
      <span className="text-foreground-800 break-all">{value}</span>
    </div>
  );
}